import { Box, Grid2, IconButton, Typography } from "@mui/material";
import PingStatusTooltip from "../../../Components/PingStatus/PingStatusTooltip";
import * as React from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ApexCharts from 'apexcharts';

import { setCurrentDatesForChart } from "../../../Actions/DigitalTwinActions";
import { getDigitalTwinChartData, getLeveledMetrics } from "../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { IDDisplayBox } from "../../../Theme/DigitalTwinStyles";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import { toTitleCase } from "../../../Helper/Formatters/TextFormatter";
import { allMetrics } from "../../../Helper/DigitalTwin/DataFile";
import VitalsSelectionDropdown from "../../../PagesContent/DigitalTwinV3/components/VitalsSelectionDropdown";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { PrimaryTooltip, StatusDot } from "../../../Components/StyledComponent";
import { changeDateFormat, getDatePickerDate, getLocalDateFromUTC } from "../../../Helper/DatePicker/DateConverter";
import SingleAlertChart from "./SingleAlertChart";
import SingleAlertDateCarousel from "./SingleAlertDateCarousel";
import { useNavigate } from "react-router-dom";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import CustomIcon from "../../../Components/CustomIcon";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../Helper/Charts/ChartHelper";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function AlertChartSection({
    batteryID,
    alertStateId,
    alertInfo = { alertBatteryNode: {} }
}) {

    const selectedNode = {
        id: alertInfo.alertBatteryNode.systemID,
        info: alertInfo.alertBatteryNode || {},
        level: alertInfo.hierarchy,
        displayId: alertInfo.affectedEntityDisplayID,
    }


    const pageContent = usePagesContent()
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation(['common', 'digital_twin'])

    const localActivatedAt = getLocalDateFromUTC(alertInfo.activatedAt, 'YYYY-MM-DD HH:mm:ss')
    const localResolvedAt = alertInfo.resolvedAt ? getLocalDateFromUTC(alertInfo.resolvedAt, 'YYYY-MM-DD HH:mm:ss') : undefined
    const localExpiredAt = alertInfo.expiredAt ? getLocalDateFromUTC(alertInfo.expiredAt, 'YYYY-MM-DD HH:mm:ss') : undefined

    const [currentAlert, setCurrentAlert] = React.useState({
        activatedAt: localActivatedAt,
        resolvedAt: localResolvedAt,
        expiredAt: localExpiredAt,
        level: alertInfo.hierarchy,
        alertStatus: alertInfo.alertStatus,
        activatedAtDate: changeDateFormat(localActivatedAt, 'YYYY-MM-DD'),
        resolvedAtDate: changeDateFormat(localResolvedAt, 'YYYY-MM-DD'),
        expiredAtDate: changeDateFormat(localExpiredAt, 'YYYY-MM-DD')
    })

    const [currentDates, setCurrentDates] = React.useState({
        startDate: getDatePickerDate(currentAlert.activatedAtDate, -1),
        endDate: currentAlert.expiredAtDate || currentAlert.resolvedAtDate || getDatePickerDate(undefined, 0),
    })

    const [primaryGranularity, setPrimaryGranularity] = React.useState('raw')
    const [secondaryGranularity, setSecondaryGranularity] = React.useState('avg')
    const [selectedMetrics, setSelectedMetrics] = React.useState([])
    const [nodeMetrics, setNodeMetrics] = React.useState(allMetrics)
    const [series, setSeries] = React.useState([])
    const [selectedDate, setSelectedDate] = React.useState({ key: 0, value: currentDates.endDate })
    const [isChartLoading, setIsChartLoading] = React.useState(true)
    const [categories, setCategories] = React.useState([])
    const [visibleSeries, setVisibleSeries] = React.useState(series.map(() => true));
    const [intervalSeconds, setIntervalSeconds] = React.useState(300);
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [zoomedRange, setZoomedRange] = React.useState(null);
    const zoomTimeoutRef = React.useRef(null);

    const chartID = 'vitals-alert-chart';

    const handleDateChange = ({ startDate, endDate }) => {
        dispatch(setCurrentDatesForChart({ startDate, endDate }))
        setCurrentDates({ startDate, endDate })
    }

    const fetchChartData = async ({ startDate, endDate, intervalSeconds }) => {
        if (!selectedNode || !selectedNode.id || !selectedMetrics || selectedMetrics.length < 1) {
            setIsChartLoading(false)
            return false
        }

        setIsChartLoading(true)

        try {
            const [seriesData, chartData, categories] = await getDigitalTwinChartData({
                batteryID: batteryID,
                startDate: startDate,
                endDate: endDate,
                selectedDate: selectedDate.value,
                primaryGranularity: primaryGranularity,
                secondaryGranularity: secondaryGranularity,
                level: selectedNode.level,
                selectedNode: selectedNode,
                metricKeys: selectedMetrics.map(metric => metric.key),
                selectedMetrics: selectedMetrics,
                intervalSeconds: intervalSeconds
            })

            setCategories(categories)
            setVisibleSeries(seriesData.map(() => true))
            setSeries(seriesData)
            setIsChartLoading(false)
        } catch (err) {
            setIsChartLoading(false)
        }
    }

    const resetChartData = async (overrideIntervalSeconds = null) => {
        const intervalToUse = overrideIntervalSeconds !== null ? overrideIntervalSeconds : intervalSeconds;

        await fetchChartData({
            startDate: currentDates.startDate,
            endDate: currentDates.endDate,
            intervalSeconds: intervalToUse
        });
    }

    const changeSelectedDateFromList = (date) => {
        // Reset zoom state when changing dates
        setIsZoomed(false);
        setZoomedRange(null);
        setIntervalSeconds(300); // Reset to default interval
        setSelectedDate({ key: selectedDate.key + 1, value: date })
    }

    const resetChartDataAfterZoom = (xaxis) => {
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        // Debounce the zoom update to prevent multiple rapid calls
        zoomTimeoutRef.current = setTimeout(async () => {

            // Use the xaxis parameter from beforeZoom event
            const minX = xaxis?.min;
            const maxX = xaxis?.max;

            if (!minX || !maxX) {
                setIsChartLoading(false);
                return;
            }

            // Calculate the appropriate interval based on the visible time window
            const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

            // Convert minX and maxX timestamps to date strings for the API
            const zoomedStartDate = formatChartTimestampForApi(minX);
            const zoomedEndDate = formatChartTimestampForApi(maxX);

            // Store zoom state
            setIsZoomed(true);
            setZoomedRange({ minX, maxX });
            setIntervalSeconds(calculatedInterval);

            // Fetch new data with the zoomed range
            await fetchChartData({
                startDate: zoomedStartDate,
                endDate: zoomedEndDate,
                intervalSeconds: calculatedInterval
            });
        }, 500); // 500ms debounce
    }

    const handleZoomReset = async () => {
        // Reset interval to default (300 seconds / 5 minutes)
        const defaultInterval = 300;
        setIntervalSeconds(defaultInterval);

        // Reset zoom state
        setIsZoomed(false);
        setZoomedRange(null);

        // Refetch chart data with original date range and default interval
        await resetChartData(defaultInterval);
    }

    const resetChartToLatestData = () => {
        if (!alertInfo.alertBatteryNode.last_pinged_time) {
            alert("No data available for selected date")
        }

        let lastPingDate = getLocalDateFromUTC(alertInfo.alertBatteryNode.last_pinged_time)

        let startDateNew = getDatePickerDate(lastPingDate, 0)
        let endDateNew = getDatePickerDate(lastPingDate, 0)

        handleDateChange({ startDate: startDateNew, endDate: endDateNew })
    }

    const toggleSeriesOnLegendClick = (index) => {
        const updatedVisibility = [...visibleSeries];
        updatedVisibility[index] = !updatedVisibility[index];
        setVisibleSeries(updatedVisibility);
        const seriesName = series[index].name;

        if (updatedVisibility[index]) {
            ApexCharts.exec(chartID, 'showSeries', seriesName);
        } else {
            ApexCharts.exec(chartID, 'hideSeries', seriesName);
        }


        if (secondaryGranularity != 'avg') {
            if (updatedVisibility[index]) {
                ApexCharts.exec(chartID, 'showSeries', `${seriesName} mix`);
            } else {
                ApexCharts.exec(chartID, 'hideSeries', `${seriesName} mix`);
            }
        }
    };

    const setupInitialMetrics = () => {
        if (!selectedNode.level) {
            return null
        }

        let nodeLeveledMetrics = getLeveledMetrics(selectedNode.level, pageContent, t)
        setSelectedMetrics(nodeLeveledMetrics.slice(0, 4));
        setNodeMetrics(nodeLeveledMetrics);
    };

    const applySelectedMetrics = (items) => {
        setSelectedMetrics(items)
    }

    const getSelectedDate = () => {
        return currentAlert.expiredAtDate || currentAlert.resolvedAtDate || currentAlert.activatedAtDate
    }

    const redirectToBatteryVitals = (batteryID) => {
        let temp = {
            batteryID: batteryID,
            batterySearchedGlobal: "false",
            navigateFromTab: true,
            tabValue: 'batteryVitals',
            startDate: getSelectedDate(),
            endDate: currentDates.endDate,
            selecetedNodeId: selectedNode.id,
        }
        encryptTheParams(temp, navigate, true);
    }

    React.useEffect(() => {
        setupInitialMetrics()
    }, [alertStateId])


    React.useEffect(() => {
        resetChartData();
    }, [selectedDate, selectedMetrics]);


    if (!selectedNode || !selectedNode.id) {
        return <CartLoader />;
    }

    const metricNames = selectedMetrics.map((metric) => metric.name)

    return (
        <Grid2 sx={{ padding: '0 32px 32px 32px', width: '100%' }}>
            <Grid2 container justifyContent={'space-between'} sx={{ mb: 1.5 }}>
                <Grid2 container gap={'8px'}>
                    <Grid2 container alignItems={'center'}>
                        <Typography variant="fw500s14c000000">Battery Vitals Behaviour </Typography>
                    </Grid2>
                </Grid2>

                <Grid2 container gap={1} alignItems={'center'} paddingRight={1}>
                    <IDDisplayBox>

                        {selectedNode?.level?.toLowerCase() === 'pack' &&
                            <PingStatusTooltip online={selectedNode?.info?.pingStatus?.toLowerCase() === 'active'} entityLabel={toTitleCase(t(`digital_twin:${selectedNode.level}`, selectedNode.level))}>
                                <StatusDot sx={{ marginRight: '0px' }} color={selectedNode?.info?.pingStatus?.toLowerCase() === 'active' ? '#09B820' : '#8F8F8F'} />
                            </PingStatusTooltip>}
                        <Box>
                            <Typography variant="fw400s12c494949">{toTitleCase(t(`digital_twin:${selectedNode.level}`, selectedNode.level))} ID: </Typography>
                            <Typography variant="fw400s12c494949">{selectedNode.displayId}</Typography>
                        </Box>
                    </IDDisplayBox>
                    <PrimaryTooltip title="Go to Battery Vitals" position="bottom">
                        <IconButton onClick={() => redirectToBatteryVitals(batteryID)} sx={{ padding: '7px' }}>
                            <CustomIcon name='redirect-icon' sx={{ color: "#6D72F6", fontSize: "18px" }} />
                        </IconButton>
                    </PrimaryTooltip>

                </Grid2>

            </Grid2>

            <Grid2 container alignItems={'center'} gap={'12px'} sx={{ mt: 1 }}>
                <VitalsSelectionDropdown
                    itemList={nodeMetrics}
                    selectedItems={selectedMetrics}
                    applySelectedItems={applySelectedMetrics}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {series.filter((s, i) => metricNames.includes(s.name)).map((s, i) => (
                        <Box
                            key={i}
                            onClick={() => toggleSeriesOnLegendClick(i)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                opacity: visibleSeries[i] ? 1 : 0.5
                            }}>
                            <StatusDot sx={{ marginRight: '8px' }} color={s.color} />
                            <Typography variant="fw400s12c3D4B5A">{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Grid2 >

            <Box sx={{ mt: 1.5 }}>
                <SingleAlertChart
                    chartID={chartID}
                    metrics={selectedMetrics}
                    currentAlert={currentAlert}
                    key={isChartLoading}
                    isLoading={isChartLoading}
                    series={series}
                    categories={categories}
                    startDate={selectedDate}
                    primaryGranularity={primaryGranularity}
                    resetToLatestDataDate={() => resetChartToLatestData()}
                    visibleSeries={visibleSeries}
                    onZoom={resetChartDataAfterZoom}
                    onZoomReset={handleZoomReset}
                    zoomedRange={zoomedRange}
                    setLoading={setIsChartLoading}
                    isZoomed={isZoomed}
                />
            </Box>

            <Box sx={{ opacity: isChartLoading ? 0 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: '50%' }}>
                    <SingleAlertDateCarousel
                        alertInfo={alertInfo}
                        currentAlert={currentAlert}
                        primaryGranularity={primaryGranularity}
                        currentDates={currentDates}
                        onChangeDate={(date) => changeSelectedDateFromList(date)}
                    />
                </Box>
            </Box>
        </Grid2 >
    )
}
